import React from 'react';
import { sizes } from '@sharefiledev/antd-config';
import { PiletApi } from '@sharefiledev/sharefile-appshell';
import { App, Button, Checkbox, Skeleton, Space, Typography } from 'antd';
import { openDesktopApplication } from '../../Addins/addin';
import { SpawnMicrosoftOfficeShareFileLogin } from '../../Addins/OpenInDesktopService';
import { useAppStore } from '../../common/store';
import {
	EditDesktopModalExtensionSlotName,
	learnMoreEditingUrl,
	localStorageSignedInOnMSDesktopApp,
} from '../../constants';
import { CoreExtensionSlotParams } from '../../types';
import { t } from '../../utils';
import { getMicrosoftOfficeAppName } from '../../utils/file';
import { ModalFooter } from './ModalFooter';

const { Text, Link, Title } = Typography;

export function SignInToShareFileModalTitle() {
	return <Title level={3}>{t('edit.signInSharefileTitle')}</Title>;
}

interface SignInToShareFileModalBodyProps {
	app: PiletApi;
	cancelInteraction?: () => void;
	completeInteraction: () => void;
}

export function SignInToShareFileModalBody({
	app,
	cancelInteraction,
	completeInteraction,
}: SignInToShareFileModalBodyProps) {
	const { documentTemplate, user, setIsLoading, uploadDocxFileModalCloseHandlers } =
		useAppStore(state => ({
			documentTemplate: state.documentTemplate,
			user: state.user,
			setIsLoading: state.setIsLoading,
			uploadDocxFileModalCloseHandlers: state.uploadDocxFileModalCloseHandlers,
		}));

	const { notification } = App.useApp();
	const localStorageKey = user.Id + localStorageSignedInOnMSDesktopApp;
	const [isSignedIn, setIsSignedIn] = React.useState(
		localStorage.getItem(localStorageKey) === 'true'
	);
	const handleCheckboxChange = () => {
		const newIsSignedIn = !isSignedIn;
		localStorage.setItem(localStorageKey, newIsSignedIn ? 'true' : 'false');
		setIsSignedIn(newIsSignedIn);
	};

	const fileName = documentTemplate?.file?.fileName;

	const openEditDesktopModal = () => {
		app.showModal(EditDesktopModalExtensionSlotName);
	};

	const onClick = async () => {
		try {
			setIsLoading(true);
			await openDesktopApplication();
			uploadDocxFileModalCloseHandlers?.completeInteraction();
			setTimeout(() => {
				openEditDesktopModal();
			}, 2000);
			completeInteraction();
		} catch {
			notification.error({
				message: t('genericError'),
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<>
			<Space direction="vertical" size={25} style={{ display: 'flex' }}>
				<Text>
					{t('edit.signInMessage', {
						appType: getMicrosoftOfficeAppName(fileName),
					})}
					<Link href={learnMoreEditingUrl} target="_blank" rel="noopener noreferrer">
						{t('edit.learnMoreText')}
					</Link>
				</Text>
				<Text>{t('edit.signInPostScriptText')}</Text>
				<Button
					type="default"
					onClick={() => {
						SpawnMicrosoftOfficeShareFileLogin();
					}}
				>
					{t('edit.signInText')}
				</Button>
				<Text>
					<Checkbox
						checked={isSignedIn}
						onChange={handleCheckboxChange}
						style={{
							marginRight: sizes.SM,
						}}
					>
						{t('edit.signInCheckboxText')}
					</Checkbox>
				</Text>
			</Space>
			<ModalFooter
				primaryDisabled={!isSignedIn}
				onPrimaryButtonClick={() => {
					onClick();
				}}
				onDefaultButtonClick={() => {
					cancelInteraction();
				}}
				primaryText={t('edit.editDesktopTitle', {
					appType: getMicrosoftOfficeAppName(fileName),
				})}
				defaultText={t('edit.cancelText')}
			/>
		</>
	);
}

const LazySignInToShareFileModalBody = React.lazy(() =>
	import('./SignInToShareFileModal').then(module => ({
		default: module['SignInToShareFileModalBody'],
	}))
);

export const AsyncLazySignInToShareFileModalBody: React.FC<
	CoreExtensionSlotParams<any>
> = props => {
	return (
		<React.Suspense fallback={<Skeleton />}>
			<LazySignInToShareFileModalBody {...props} />
		</React.Suspense>
	);
};
